import { useEffect, useState, useContext, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getIndexByUserId } from "../../helpers/http_payment_methods_requests";
import CustomerSelection from "./components/CustomerSelection";
import ProductManagement from "./components/ProductManagement";
import PaymentMethodManagement from "./components/PaymentMethodManagement";
import { Box, Card } from "@mui/material";
import { GoldPriceContext } from "../../providers/GoldPriceProvider";
import { createOrder, printOrderProof } from "../../helpers/http_orders_requests";
import { useAuth } from "../../providers/AuthProvider";
import { useNavigate } from "react-router-dom";

export default function OrderNew() {

    const { id, user } = useAuth();

    const navigate = useNavigate();

    const { currentGoldPrice, currentSilverPrice } = useContext(GoldPriceContext);

    const [currentTotalPrice, setCurrentTotalPrice] = useState(0.0);

    const [currentCustomerId, setCurrentCustomerID] = useState(null);
    const [customerPriceGroup, setCustomerPriceGroup] = useState(null);

    const [productList, setProductList] = useState([]);
    const [paymentList, setPaymentList] = useState([]);

    const priceGroupQuery = useQuery({
        queryKey: ['getIndexByUserId', currentCustomerId],
        queryFn: () => getIndexByUserId(currentCustomerId),
        enabled: false,
    });

    useEffect(() => {
        if (currentCustomerId) {
            priceGroupQuery.refetch();
        };
    }, [currentCustomerId]);

    useEffect(() => {
        if (priceGroupQuery.isSuccess && priceGroupQuery.data) {
            setCustomerPriceGroup(priceGroupQuery.data.data.data);
        }
    }, [priceGroupQuery.data]);

    useEffect(() => {

        if (productList.length >= 1) {
            handleUpdateTotalPrice();
        } else {
            setCurrentTotalPrice(0);
        };
    }, [productList]);

    useEffect(() => {

        setProductList([])

    }, [currentCustomerId]);

    const calculateJewelPrice = (product) => {

        if (!product.is_service) {

            const prodMaterial = product.selected_metal;

            const prodIsCrafted = product.is_crafted;

            const prodWeightProp = {
                1: 'high_gold_weight',
                2: 'low_gold_weight',
                3: 'silver_weight',
            };

            const customerIndexProp = {
                "high_gold_weight": prodIsCrafted ? 'high_gold_crafted_index' : "high_gold_index",
                "low_gold_weight": prodIsCrafted ? 'low_gold_crafted_index' : "low_gold_index",
                "silver_weight": prodIsCrafted ? 'silver_crafted_index' : "silver_index",
            };

            const currentMetalPrice = {
                1: currentGoldPrice,
                2: currentGoldPrice,
                3: currentSilverPrice,
            };

            const weight = Number(product[prodWeightProp[prodMaterial]]) || 0;
            const index = Number(customerPriceGroup[customerIndexProp[prodWeightProp[prodMaterial]]]) || 0;
            const spotCents = Number(currentMetalPrice[prodMaterial]) || 0;

            return Math.round(weight * index * spotCents);
        };

        return Math.round(Number(product.price) || 0);


    };

    const handleUpdateTotalPrice = () => {

        var totalPrice = 0;

        productList.forEach(product => {

            if (!product.is_service) {

                const extras = product.extras;

                const stones = product.stones;

                if (extras.length >= 1) {

                    extras.forEach(extra => {
                        totalPrice += extra.price;

                    })
                };

                if (stones.length >= 1) {
                    stones.forEach(stone => {
                        var quantity = Number(stone.quantity);
                        var unitPrice = Number(stone.price);

                        var stoneAmountPrice = unitPrice * quantity;

                        totalPrice += stoneAmountPrice;
                    })
                }

                totalPrice += product.price;

            } else {

                totalPrice += parseFloat(product.price);

            };


        });

        setCurrentTotalPrice(totalPrice);
    };

    const handleIncludeProduct = (data) => {

        setProductList((oldArray) => [...oldArray, { ...data, price: calculateJewelPrice(data) }]);
    };

    const handleIncludePayment = (data) => {
        setPaymentList((oldArray) => [...oldArray, data]);
    };

    const handleRemovePayment = (targetIndex) => {
        setPaymentList((prev) => prev.filter((_, index) => index !== targetIndex));
    };

    const handleResetPayment = useCallback(() => {
        setPaymentList([]);
    }, []);

    const handleRemoveProduct = (targetIndex) => {
        setProductList((prev) => prev.filter((_, index) => index !== targetIndex));
    };

    const handleSetCustomerId = (id) => setCurrentCustomerID(id);

    const handleCreateOrder = () => {

        const payload = {
            customer_id: currentCustomerId,
            user_id: id,
            attendant_name: user?.complete_name,
            products: productList.map(prod => ({
                ...prod,
                selected_metal_name: prod.selected_metal_name
            })),
            total_price: Math.round(currentTotalPrice),
            payment_methods: paymentList
        };

        createOrder(payload, () => navigate('/pedidos'));
    };

    return (
        <Box sx={styles.gridContainer}>
            <Box sx={styles.gridItemSpan2}>
                <Card sx={styles.card}>
                    <ProductManagement isCustomerDefined={currentCustomerId} currentTotalPrice={currentTotalPrice} handleRemoveProduct={handleRemoveProduct} productList={productList} handleIncludeProduct={handleIncludeProduct} />
                </Card>
            </Box>
            <Box sx={styles.gridItem}>
                <Card sx={styles.card}>
                    <CustomerSelection handleSetCustomerId={handleSetCustomerId} />
                </Card>
            </Box>
            <Box sx={styles.gridItem}>
                <Card sx={styles.card}>
                    <PaymentMethodManagement handleRemovePayment={handleRemovePayment} handleResetPayment={handleResetPayment} paymentList={paymentList} handleIncludePayment={handleIncludePayment} isPaymentAvailable={currentTotalPrice > 0} hasProducts={productList.length > 0} isCustomerDefined={currentCustomerId} currentTotalPrice={currentTotalPrice} handleCreateOrder={handleCreateOrder} />
                </Card>
            </Box>
        </Box>
    );
};

const styles = {
    gridContainer: {
        minHeight: '86vh',
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: 2,
    },
    gridItem: {
        gridColumn: {
            xs: "span 12",
            lg: "span 6",
        },
    },
    gridItemSpan2: {
        gridColumn: {
            xs: "span 12",
            lg: "span 6",
        },
        gridRow: { lg: "span 2" },
        height: "100%",
        minHeight: 0,
    },
    card: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
};
